import { NgTemplateOutlet } from '@angular/common';
import {
  computed,
  DestroyRef,
  inject,
  signal,
  TemplateRef,
  viewChild,
  ChangeDetectorRef,
  Component,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';

import { InfoboxComponent } from '@core/components-infobox-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import type { DialogComponentI, SelectOption } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  FormFieldComponent,
  LabelComponent,
  NewItemButtonComponent,
  SelectComponent,
} from '@core/user-interface-legacy';

type DialogData = {
  jobCategories: string[];
  jobsCount: number;
};

export type ManageEeoDialogResult =
  | {
      action: 'enable' | 'disable';
      jobCategory?: string;
    }
  | undefined;

enum Step {
  Action = 1,
  Category = 2,
}

@Component({
  selector: 'rt-manage-eeo-dialog',
  templateUrl: './manage-eeo-dialog.component.html',
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    NgTemplateOutlet,
    DialogFooterComponent,
    ButtonComponent,
    NewItemButtonComponent,
    FormFieldComponent,
    LabelComponent,
    SelectComponent,
    TranslatePipe,
    InfoboxComponent,
  ],
})
export class ManageEeoDialogComponent implements DialogComponentI<
  DialogData,
  ManageEeoDialogResult
> {
  private readonly dialogRef = inject(DialogRef);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  public stepAction = viewChild<TemplateRef<void>>('stepAction');
  public stepCategory = viewChild<TemplateRef<void>>('stepCategory');

  protected jobsCount = signal(0);
  protected categoryOptions = signal<SelectOption[]>([]);
  protected currentStep = signal(Step.Action);

  protected categoryControl = new FormControl('', [Validators.required]);

  protected hasFooter = computed(() => this.currentStep() === Step.Category);
  protected bodyForCurrentStep = computed(() => {
    switch (this.currentStep()) {
      case Step.Action:
        return this.stepAction()!;
      case Step.Category:
        return this.stepCategory()!;
      default:
        return null;
    }
  });

  public setDialogData(data: DialogData): void {
    const { jobCategories, jobsCount } = data;
    this.categoryOptions.set(jobCategories.map(category => ({ label: category, value: category })));
    this.jobsCount.set(jobsCount);
  }

  protected ngOnInit(): void {
    this.categoryControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cdRef.detectChanges());
  }

  protected enable() {
    this.currentStep.set(Step.Category);
  }

  protected disable() {
    this.dialogRef.close({
      action: 'disable',
    });
  }

  protected save() {
    this.dialogRef.close({
      action: 'enable',
      jobCategory: this.categoryControl.value,
    });
  }

  protected cancel() {
    this.dialogRef.close();
  }
}
